"use client"

import { useState, useEffect } from "react"
import { useInView } from "react-intersection-observer"
import PaintSplatter from "./paint-splatter"
import { useAuth } from "../../AuthContext"
import { useNavigate } from "react-router-dom"
import { encryptId } from "../../utils/crypto" // Import encryptId

export default function Skills({ profile }) {
  const [activeTab, setActiveTab] = useState("experience")
  const { user } = useAuth()
  const [isOwner, setIsOwner] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (user && profile) {
      setIsOwner(user.id === profile.user_id)
    }
  }, [user, profile]) // Run only when `user` or `profile` changes

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })

  const handleEditSkill = (skillId) => {
    alert("you can't edit skill but you can delete and insert new skill.")
  }

  const handleRemoveSkill = async (proSkillId) => {
    if (window.confirm("Are you sure you want to remove this skill?")) {
      try {
        console.log("Removing skill:", proSkillId); // Log the skill ID being removed
        const response = await fetch(`http://localhost:5000/delete-proskills?proSkillId=${proSkillId}`, {
          method: "DELETE",
        });
        const result = await response.json();
        if (response.ok) {
          alert(result.message);
          // Encrypt user.id before navigating
          navigate(`/ProProject`);
        } else {
          alert(`Failed to remove skill: ${result.error}`);
        }
      } catch (error) {
        console.error("Error removing skill:", error);
        alert("An error occurred while removing the skill.");
      }
    }
  }

  const handleEditEducation = (educationId) => {
    const educationRecord = profile?.education.find((edu) => edu.education_id === educationId)
    if (educationRecord) {
      console.log("Edit education:", educationRecord)
      navigate("/AddEducation", { state: { educationRecord } })
    }
  }

  const handleRemoveEducation = async (educationId) => {
    if (window.confirm("Are you sure you want to remove this education record?")) {
      try {
        const response = await fetch(`http://localhost:5000/delete-education/${educationId}`, { // Fix URL
          method: "DELETE",
        });
        const result = await response.json();
        if (response.ok) {
          alert(result.message);
          // Encrypt user.id before navigating
          navigate(`/ProProject`);
        } else {
          alert(`Failed to remove education record: ${result.error}`);
        }
      } catch (error) {
        console.error("Error removing education record:", error);
        alert("An error occurred while removing the education record.");
      }
    }
  }

  const handleEditExperience = (experienceId) => {
    const experienceRecord = profile?.experience.find((exp) => exp.experience_id === experienceId);
    if (experienceRecord) {
      console.log("Edit experience:", experienceRecord);
      navigate("/AddWorkExperience", { state: { experienceRecord } });
    }
  }

  const handleRemoveExperience = async (experienceId) => {
    if (window.confirm("Are you sure you want to remove this experience?")) {
      try {
        const response = await fetch(`http://localhost:5000/delete-work-experience?exId=${experienceId}`, {
          method: "DELETE",
        })
        const result = await response.json()
        if (response.ok) {
          alert(result.message)
          // Encrypt user.id before navigating
          navigate(`/ProProject`);
        } else {
          alert(`Failed to remove experience: ${result.error}`)
        }
      } catch (error) {
        console.error("Error removing experience:", error)
        alert("An error occurred while removing the experience.")
      }
    }
  }

  const containerStyle = {
    padding: "4rem 1rem 1.25rem",
    position: "relative",
  }

  const flexContainerStyle = {
    display: "flex",
    flexWrap: "wrap",
    margin: "0 -1rem",
    position: "relative",
    maxWidth: "1200px",
    margin: "0 auto",
    zIndex: 10,
  }

  const columnStyle = {
    width: "100%",
    padding: "0 1rem",
    marginBottom: "2.5rem",
    animation: "fadeIn 1s ease-out forwards",
  }

  const headingStyle = {
    fontSize: "2.25rem",
    marginBottom: "1.25rem",
    fontWeight: "bold",
  }

  const paragraphStyle = {
    marginBottom: "1rem",
  }

  const skillsHeadingStyle = {
    marginBottom: "1rem",
  }

  const skillsGridStyle = {
    display: "flex",
    flexWrap: "wrap",
    margin: "0 -0.5rem",
  }

  const skillsColumnStyle = {
    width: "100%",
    padding: "0 0.5rem",
  }

  const skillItemStyle = {
    marginBottom: "1rem",
  }

  const skillHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
  }

  const skillNameStyle = {
    fontWeight: "semibold",
  }

  const skillPercentStyle = {
    fontWeight: "semibold",
  }

  const skillBarContainerStyle = {
    height: "0.5rem",
    backgroundColor: "#e5e7eb",
    borderRadius: "0.25rem",
    overflow: "hidden",
  }

  const getSkillBarStyle = (percent) => ({
    height: "100%",
    width: inView ? `${percent}%` : "0%",
    transition: "width 1s",
    backgroundColor: "#7749F8",
  })

  const getSkillBarColorStyle = (color) => ({
    backgroundColor: color,
  })

  const tabsContainerStyle = {
    border: "2px solid #7749F8",
    borderRadius: "0.25rem",
    marginBottom: "1.25rem",
    overflow: "hidden",
  }

  const tabsStyle = {
    display: "flex",
  }

  const tabButtonStyle = {
    width: "50%",
    padding: "0.75rem",
    fontSize: "1.25rem",
    fontWeight: "500",
    backgroundColor: "white",
    border: "none",
    cursor: "pointer",
  }

  const activeTabStyle = {
    backgroundColor: "#7749F8",
    color: "white",
  }

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "1.25rem",
  }

  const experienceItemStyle = {
    marginBottom: "1rem",
  }

  const experienceHeadingStyle = {
    fontSize: "1.125rem",
    fontWeight: "500",
  }

  const experienceDividerStyle = {
    borderTop: "1px solid #7749F8",
    margin: "0.5rem 0",
  }

  const experienceDateStyle = {
    color: "#7749F8",
    marginBottom: "0.25rem",
  }

  const experienceCompanyStyle = {
    fontWeight: "500",
    marginBottom: 0,
  }

  useEffect(() => {
    const handleResize = () => {
      const isLargeScreen = window.innerWidth >= 1024
      const isMediumScreen = window.innerWidth >= 768

      const leftColumn = document.getElementById("skills-left-column")
      const rightColumn = document.getElementById("skills-right-column")
      const skillColumns = document.querySelectorAll(".skills-column")
      const gridContainer = document.getElementById("experience-grid")

      if (leftColumn && rightColumn) {
        if (isLargeScreen) {
          leftColumn.style.width = "50%"
          rightColumn.style.width = "50%"
          leftColumn.style.marginBottom = "0"

          if (gridContainer) {
            gridContainer.style.gridTemplateColumns = "1fr 1fr"
          }
        } else {
          leftColumn.style.width = "100%"
          rightColumn.style.width = "100%"

          if (gridContainer) {
            gridContainer.style.gridTemplateColumns = isMediumScreen ? "1fr 1fr" : "1fr"
          }
        }
      }

      if (skillColumns.length) {
        skillColumns.forEach((column) => {
          column.style.width = isMediumScreen ? "50%" : "100%"
        })
      }
    }

    handleResize()
    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  return (
    <div style={containerStyle} id="skill" ref={ref}>
      <PaintSplatter position="bottom-right" size="md" opacity={0.15} rotation={15} />
      <PaintSplatter position="top-left" size="sm" opacity={0.2} rotation={-20} />

      <div style={flexContainerStyle}>
        <div id="skills-left-column" style={columnStyle}>
          <h1 style={headingStyle}>Skills & Experience</h1>
          <h3 style={skillsHeadingStyle}>My Skills</h3>
          <div style={skillsGridStyle}>
            {(profile?.skills || []).map((skill, index) => (
              <div key={index} className="skills-column" style={skillsColumnStyle}>
                <div style={skillItemStyle}>
                  <div style={skillHeaderStyle}>
                    <h6 style={skillNameStyle}>{skill.skill_name}</h6>
                    <h6 style={skillPercentStyle}>{skill.experience} years</h6>
                    {isOwner && (
                      <div>
                        <button onClick={() => handleEditSkill(skill.professional_skill_id)} title="Edit">
                          ✏️
                        </button>
                        <button onClick={() => handleRemoveSkill(skill.professional_skill_id)} title="Remove">
                          ❌
                        </button>
                      </div>
                    )}
                  </div>
                  <div style={skillBarContainerStyle}>
                    <div style={getSkillBarStyle(skill.experience * 10)}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div id="skills-right-column" style={columnStyle}>
          <div style={tabsContainerStyle}>
            <div style={tabsStyle}>
              <button
                style={{
                  ...tabButtonStyle,
                  ...(activeTab === "experience" ? activeTabStyle : {}),
                }}
                onClick={() => setActiveTab("experience")}
              >
                Experience
              </button>
              <button
                style={{
                  ...tabButtonStyle,
                  ...(activeTab === "education" ? activeTabStyle : {}),
                }}
                onClick={() => setActiveTab("education")}
              >
                Education
              </button>
            </div>
          </div>
          <div>
            {activeTab === "experience" && (
              <div id="experience-grid" style={gridStyle}>
                {(profile?.experience || []).map((exp, index) => (
                  <div key={index} style={experienceItemStyle}>
                    <h5 style={experienceHeadingStyle}>{exp.role}</h5>
                    <hr style={experienceDividerStyle} />
                    <p style={experienceDateStyle}>{exp.start_date} - {exp.end_date}</p>
                    <h6 style={experienceCompanyStyle}>{exp.company_name}</h6>
                    {isOwner && (
                      <div>
                        <button onClick={() => handleEditExperience(exp.experience_id)} title="Edit">
                          ✏️
                        </button>
                        <button onClick={() => handleRemoveExperience(exp.experience_id)} title="Remove">
                          ❌
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {activeTab === "education" && (
              <div id="education-grid" style={gridStyle}>
                {(profile?.education || []).map((edu, index) => (
                  <div key={index} style={experienceItemStyle}>
                    <h5 style={experienceHeadingStyle}>{edu.degree} in {edu.field_of_study}</h5>
                    <hr style={experienceDividerStyle} />
                    <p style={experienceDateStyle}>{edu.start_date} - {edu.end_date}</p>
                    <h6 style={experienceCompanyStyle}>{edu.institution_name}</h6>
                    {isOwner && (
                      <div>
                        <button onClick={() => handleEditEducation(edu.education_id)} title="Edit">
                          ✏️
                        </button>
                        <button onClick={() => handleRemoveEducation(edu.education_id)} title="Remove">
                          ❌
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

